import { ELEMENTS } from '@/lib/experience'
import { motion } from 'framer-motion'

interface Props {
  onRestart: () => void
  onProtocol: () => void
}

const MAPA_LINK = 'http://elementos.reacendaluz.com.br/mapa'

/**
 * Special screen shown when all 4 elements score 0 (every answer was Option B).
 * The tiebreaker rule is NOT applied here — instead we celebrate the user's
 * current balance and still present both offers normally.
 *
 * Visual identity is intentionally aligned with StepOffers so the experience
 * feels continuous even on this alternate ending.
 */
function StepBalanced({ onRestart, onProtocol }: Props) {
  // Neutral palette for the "balanced" state — soft, luminous, un-biased toward any element.
  const balancedColor = '#B9A3D9'       // subtle lilac-white (airy)
  const balancedSoft  = '#D4C3EA'

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-3xl mx-auto"
    >
      {/* ── HEADER ── */}
      <div className="text-center mb-12 md:mb-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="text-[10px] tracking-[0.4em] uppercase font-body mb-5"
          style={{ color: balancedSoft }}
        >
          Sua leitura · Campo em equilíbrio
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="font-display type-hero text-[var(--ink)] mb-6"
        >
          Seu campo está{' '}
          <em className="italic font-light" style={{ color: balancedSoft }}>
            equilibrado.
          </em>
        </motion.h1>

        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 100, opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.4 }}
          className="h-px mx-auto mb-7"
          style={{
            background: `linear-gradient(90deg, transparent, ${balancedSoft}, transparent)`,
          }}
        />

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="font-body type-body text-[var(--ink-soft)] max-w-xl mx-auto"
        >
          De acordo com suas respostas seus elementos não apresentaram bloqueios detectáveis neste momento. Isso é um sinal positivo. Continue cultivando esse equilíbrio. Se em algum momento sentir que algo mudou volte e refaça a experiência.
        </motion.p>

        {/* Four balanced dots, one per element, as a quiet visual signature. */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="flex items-center justify-center gap-2 mt-8 opacity-70"
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: ELEMENTS.terra.accentSoft }} />
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: ELEMENTS.agua.accentSoft }} />
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: ELEMENTS.fogo.accentSoft }} />
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: ELEMENTS.ar.accentSoft }} />
        </motion.div>
      </div>

      {/* ── OFFERS GRID (same structure as StepOffers) ── */}
      <div className="flex flex-col gap-6 md:gap-8">

        {/* ── OFERTA 1 — PROTOCOLO ── */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="card-elevated rounded-sm p-5 sm:p-6 md:p-10 relative overflow-hidden"
        >
          <div
            className="absolute inset-x-0 -top-32 h-64 pointer-events-none"
            style={{
              background: `radial-gradient(closest-side, ${balancedSoft}18, transparent)`,
            }}
          />

          <div className="flex items-center gap-3 mb-7">
            <span
              className="text-[10px] tracking-[0.4em] uppercase font-body px-3 py-1 rounded-full"
              style={{
                color: balancedSoft,
                border: `1px solid ${balancedSoft}44`,
                background: `${balancedColor}12`,
              }}
            >
              Oferta <span className="numerals">1</span>
            </span>
            <div
              className="flex-1 h-px"
              style={{
                background: `linear-gradient(90deg, ${balancedSoft}44, transparent)`,
              }}
            />
          </div>

          <h2 className="font-display type-section text-[var(--ink)] mb-6">
            Protocolo de{' '}
            <em className="italic font-light" style={{ color: balancedSoft }}>
              Liberação e Cocriação
            </em>
          </h2>

          <div className="space-y-4 mb-8">
            {[
              'O Protocolo de Liberação e Cocriação vai além do diagnóstico.',
              'Você recebe uma meditação específica para desbloquear os padrões que estão limitando sua vida e ao mesmo tempo potencializar os elementos que estão em desequilíbrio e que fazem você repetir ciclos negativos. Essa meditação é escutada por 21 noites consecutivas antes de dormir, no momento em que o cérebro transita para o estado theta, a única janela do dia em que o inconsciente fica diretamente acessível sem o filtro do julgamento consciente. É exatamente o estado que hipnoterapeutas induzem artificialmente e que acontece naturalmente toda noite.',
              'Antes de receber a meditação você preenche um formulário de investigação profunda das crenças, programações familiares herdadas e padrões que se repetem na sua vida. Com base nesse formulário você recebe um Mapa de Revelações, um documento personalizado que nomeia os padrões e as dores que fazem você repetir os mesmos ciclos, e a Trindade de Cura específica para o que foi identificado.',
              'A meditação é gravada com intenção direcionada para a sua consciência. Antes de gravar me conecto com o seu campo vibracional para que a frequência dessa gravação chegue até você potencializada desde a primeira noite em que escutar.',
              'A neurociência documenta que a repetição consistente de novos padrões por 21 dias começa a reconfigurar redes neurais. O HeartMath Institute documenta que gravações feitas em estado de coerência cardíaca carregam uma qualidade que afeta o sistema nervoso de quem escuta. Você não está apenas ouvindo uma meditação. Está recebendo um campo de intenção real direcionado para o que está pronto para se dissolver em você.',
            ].map((p, i) => (
              <p key={i} className="font-body type-body text-[var(--ink-soft)]">
                {p}
              </p>
            ))}
          </div>

          <div className="pt-7 border-t" style={{ borderColor: `${balancedSoft}22` }}>
            <p
              className="font-display text-[20px] sm:text-[22px] md:text-[28px] leading-none mb-6"
              style={{ color: balancedSoft }}
            >
              A partir de{' '}
              <span className="text-[var(--ink)] numerals">R$297</span>
            </p>

            <button
              onClick={onProtocol}
              className="block w-full text-center btn-primary px-4 sm:px-8 py-4 min-h-[52px] rounded-sm type-button tracking-[0.1em] sm:tracking-[0.15em] uppercase"
            >
              Quero conhecer o Protocolo Completo
            </button>
          </div>
        </motion.div>

        {/* ── OFERTA 2 — MAPA ── */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0 }}
          className="card-elevated rounded-sm p-5 sm:p-6 md:p-10 relative overflow-hidden"
        >
          <div
            className="absolute left-0 inset-y-0 w-[3px] rounded-l-sm"
            style={{
              background: `linear-gradient(180deg, ${balancedColor}88, ${balancedSoft}44, transparent)`,
            }}
          />

          <div className="flex items-center gap-3 mb-7">
            <span
              className="text-[10px] tracking-[0.4em] uppercase font-body px-3 py-1 rounded-full"
              style={{
                color: balancedSoft,
                border: `1px solid ${balancedSoft}44`,
                background: `${balancedColor}0e`,
              }}
            >
              Oferta <span className="numerals">2</span>
            </span>
            <div
              className="flex-1 h-px"
              style={{
                background: `linear-gradient(90deg, ${balancedSoft}44, transparent)`,
              }}
            />
          </div>

          <h2 className="font-display type-section text-[var(--ink)] mb-6">
            Mapa de Cura dos{' '}
            <em className="italic font-light" style={{ color: balancedSoft }}>
              <span className="numerals">4</span> Elementos
            </em>
          </h2>

          <p className="font-body type-body text-[var(--ink-soft)] mb-8">
            O Mapa vai à raiz de todos os seus <span className="numerals">4</span> elementos. Você recebe um diagnóstico completo com seus padrões limitantes, sua Trindade de Cura personalizada e seus Decretos Eu Sou escritos especificamente para você.
          </p>

          <div className="pt-7 border-t" style={{ borderColor: `${balancedSoft}22` }}>
            <p
              className="font-display text-[20px] sm:text-[22px] md:text-[28px] leading-none mb-6"
              style={{ color: balancedSoft }}
            >
              <span className="text-[var(--ink)] numerals">R$97</span>
            </p>

            <a
              href={MAPA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center btn-primary px-4 sm:px-8 py-4 min-h-[52px] rounded-sm type-button tracking-[0.1em] sm:tracking-[0.15em] uppercase"
            >
              Quero meu Mapa de Cura
            </a>
          </div>
        </motion.div>
      </div>

      {/* Restart link */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        className="mt-14 text-center"
      >
        <button
          onClick={onRestart}
          className="text-[var(--ink-soft)] hover:text-[var(--ink)] transition-colors text-[12px] tracking-[0.2em] uppercase font-body border-b border-[var(--line)] hover:border-[var(--ink-soft)] pb-1"
        >
          Refazer a experiência
        </button>
      </motion.div>
    </motion.div>
  )
}

export default StepBalanced
